import { useState, useEffect } from "react";
import { GoabDivider, GoabButton, GoabButtonGroup, GoabModal, GoabTooltip } from "@abgov/react-components";
import { isPortalActive, isEventTypeActive, EventsEnabled } from "../../../MPP-Common-SPA/utils/globalUtility";
import { FeatureNames } from "../../../MPP-Common-SPA/featureFlags/features";
import { EventStatusEnum } from "../../../MPP-Common-SPA/programLocations/enums";
import { useGetProgramEventListQuery, useGetProgramFeatureMappingByNameQuery } from "../../../Store/api/apiSlice";
import { trackEvent } from "../../../MPP-Common-SPA/utils/analytics";
import { FINISH_LATER_EVENT } from "../../../MPP-Common-SPA/utils/analytics-constants";

export const SaveAndFinishLaterButton = ({
  type = "tertiary",
  isLoggedIn,
  getResourceValue,
  templateForm,
  fields,
  context,
  portal,
  user,
  applicationType,
  getFormValues,
  saveStepData,
  finishLater,
  onPrevious,
  currentStep,
  getRefData
}) => {
  const [showModal, setShowModal] = useState(false);

  const { data: programEventFeatureMapping, isLoading: programEventFeatureMappingIsLoading } = useGetProgramFeatureMappingByNameQuery({featureName: FeatureNames.Events, programId: portal.id}, {skip: !portal?.id});
  const { data: programEvents = [], isLoading: programEventsIsLoading } = useGetProgramEventListQuery(portal?.id, {skip: !portal?.id});

  const {
    renderer: { props } = {},
  } = currentStep ?? {};

  const { showBackButton, showStepCounter, backButtonLabel, hideFinishLaterButton, requireValidFormToFinishLater } = props ?? {};

  // Trigger validation once on step load when requireValidFormToFinishLater is true to ensure templateForm.isValid is set
  useEffect(() => {
    if (requireValidFormToFinishLater && templateForm?.validateForm) {
      templateForm.validateForm();
    }
  }, []); // Empty dependency array - run only once on mount

  if (!isLoggedIn) {
    return null;
  }

  const handlePrevious = async () => {
    const formValues = getFormValues ? getFormValues() : {};
    
    await onPrevious(formValues, fields, context, getRefData);
    requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
  };

  const handleFinishLater = async () => {
    if (templateForm) {
      saveStepData({ ...(templateForm?.values ?? {}) });
    } else if (getFormValues) {
      saveStepData({ ...(getFormValues() ?? {}) });
    }
    let stepData = undefined;
    if (getFormValues) {
      stepData = { ...(getFormValues() ?? {}) };
    }
    await finishLater(stepData);

    // Send GA event when the finish later confirm/continue button is clicked. The label is the current step when the button is clicked.
    trackEvent(FINISH_LATER_EVENT, {
      "finish_later_step": currentStep.name
    });
    setShowModal(false);
  };
  
  const activeEvents = programEvents.filter(evt => evt.isActive == true && evt.eventStatus == EventStatusEnum.Active);
  const selectedEvent = programEvents.find(evt => evt.code === applicationType);

  // Wait for data to load or check if portal/event is inactive
  if (programEventFeatureMappingIsLoading || programEventsIsLoading) {
    return <></>
  }

  const isEventsEnabledFlag = EventsEnabled(programEventFeatureMapping);
  const isActivePortalFlag = isPortalActive(portal, user);
  const isActiveEventTypeFlag = selectedEvent ? isEventTypeActive(programEventFeatureMapping, selectedEvent, user) : false;

  if ((isEventsEnabledFlag && !isActiveEventTypeFlag) || !isActivePortalFlag) {
    return <></>
  }
  
  return (
    <>
      {((showBackButton && showStepCounter) || !hideFinishLaterButton) && <GoabDivider mt="xl" mb="m"></GoabDivider>}
      <div style={{display: "flex", justifyContent: "space-between"}}>
        {showBackButton && showStepCounter ? (
          <GoabButton
            type="tertiary"
            leadingIcon="arrow-back"
            onClick={handlePrevious}
          >
            {getResourceValue({ key: backButtonLabel ? "RESOURCES@" + backButtonLabel : "RESOURCES@global.button.previous", })}
          </GoabButton>
        ) : <div></div>}
        {showBackButton && !hideFinishLaterButton && <div style={{margin: "10px"}}></div>}
        {!hideFinishLaterButton && ((requireValidFormToFinishLater && !templateForm.isValid) ? 
        <GoabTooltip position="top" hAlign="left" content={getResourceValue({ key: "RESOURCES@global.saveAndFinishLater.tooltip", })}>
          <GoabButton type={type} disabled='true'>
            {getResourceValue({ key: "RESOURCES@global.saveAndFinishLater", })}
          </GoabButton>
        </GoabTooltip>
        :
        <GoabButton type={type} onClick={() => setShowModal(true)}>
        {getResourceValue({ key: "RESOURCES@global.saveAndFinishLater", })}
      </GoabButton>)}
      </div>
      <GoabModal
        testId="saveAndFinishLater--modal"
        heading={getResourceValue({ key: "RESOURCES@saveAndFinishLater.modal.header", })}
        role="alertdialog"
        open={showModal}
        actions={
          <GoabButtonGroup alignment="end">
            <GoabButton
              type="secondary"
              name="Back"
              testId="saveAndFinishLater-back-button"
              onClick={() => setShowModal(false)}
            >
              {getResourceValue( { key: "RESOURCES@saveAndFinishLater.button.finishLater", })}
            </GoabButton>
            <GoabButton
              type="primary"
              name="continue"
              testId="saveAndFinishLater-continue-button"
              onClick={handleFinishLater}
            >
              {getResourceValue({ key: "RESOURCES@saveAndFinishLater.button.continue", })}
            </GoabButton>
          </GoabButtonGroup>
        }
      >
        <p>
          {getResourceValue({ key: "RESOURCES@" + (!user.isProxyUser ? "saveAndFinishLater.confirmation.text" : "saveAndFinishLater.proxyConfirmation.text"), })}
        </p>
      </GoabModal> 
    </>
    
  );
};
